// src/store/notifications/notificationsReducer.js
const initialState = {
  notifications: [],
  unreadCount: 0,
}

const notificationsReducer = (state = initialState, action) => {
  switch (action.type) {
    case 'SET_NOTIFICATIONS':
      // Trier les notifications par date (plus récentes en premier)
      const sortedNotifications = Array.isArray(action.payload)
        ? action.payload.sort((a, b) => {
            const dateA = new Date(a.created_at || a.createdAt)
            const dateB = new Date(b.created_at || b.createdAt)
            return dateB - dateA // Ordre décroissant (plus récent en premier)
          })
        : []

      return {
        ...state,
        notifications: sortedNotifications,
        unreadCount: sortedNotifications.filter((n) => !n.read).length,
      }

    case 'ADD_NOTIFICATION':
      // Helper pour extraire messageId du content JSON
      const getMessageIdFromContent = (notification) => {
        if (!notification) return null
        // Si message_id est directement sur l'objet
        if (notification.message_id) return notification.message_id
        // Sinon essayer de parser le content
        try {
          const content = typeof notification.content === 'string'
            ? JSON.parse(notification.content)
            : notification.content
          return content?.messageId || null
        } catch {
          return null
        }
      }

      // Vérifier si la notification existe déjà pour éviter les doublons
      const exists = state.notifications.some((n) => {
        // Même ID = doublon évident
        if (n.id === action.payload.id) return true

        // Pour les messages, comparer par messageId (dans content)
        if (n.type === 'message' && action.payload.type === 'message') {
          const existingMsgId = getMessageIdFromContent(n)
          const newMsgId = getMessageIdFromContent(action.payload)
          if (existingMsgId && newMsgId && existingMsgId === newMsgId) {
            return true
          }
        }

        // Pour les posts, vérifier par type + sender + postId
        if (n.type === action.payload.type &&
            n.sender?.id === action.payload.sender?.id &&
            n.postId === action.payload.postId &&
            (n.type === 'post_reaction' ||
             n.type === 'post_comment' ||
             n.type === 'post_tag' ||
             n.type === 'post_mention')) {
          return true
        }

        return false
      })

      if (exists) {
        // Pour les réactions, mettre à jour la notification existante
        if (action.payload.type === 'post_reaction') {
          const updatedNotifications = state.notifications.map((notification) =>
            notification.sender?.id === action.payload.sender?.id &&
            notification.postId === action.payload.postId &&
            notification.type === 'post_reaction'
              ? { ...action.payload, read: false } // Marquer comme non lue
              : notification,
          )

          // Re-trier après mise à jour
          const sortedUpdated = updatedNotifications.sort((a, b) => {
            const dateA = new Date(a.created_at || a.createdAt || a.timestamp)
            const dateB = new Date(b.created_at || b.createdAt || b.timestamp)
            return dateB - dateA
          })

          return {
            ...state,
            notifications: sortedUpdated,
            unreadCount: sortedUpdated.filter((n) => !n.read).length,
          }
        }
        return state
      }

      // Insertion ordonnée O(1) en moyenne (nouvelle notif = la plus récente dans 99% des cas)
      const getTsMs = (n) => {
        const raw = n?.created_at || n?.createdAt || n?.timestamp
        const t = Date.parse(raw)
        return Number.isFinite(t) ? t : 0
      }
      const newTs = getTsMs(action.payload)
      const existing = state.notifications
      let insertIdx = 0
      // Trouver la position d'insertion (tableau trié décroissant)
      while (insertIdx < existing.length) {
        if (getTsMs(existing[insertIdx]) <= newTs) break
        insertIdx++
      }
      const newNotifications = [
        ...existing.slice(0, insertIdx),
        action.payload,
        ...existing.slice(insertIdx),
      ]

      return {
        ...state,
        notifications: newNotifications,
        unreadCount: state.unreadCount + (action.payload.read ? 0 : 1),
      }

    case 'MARK_NOTIFICATION_READ':
      const updatedNotifications = state.notifications.map((notification) =>
        notification.id === action.payload ? { ...notification, read: true } : notification,
      )

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: updatedNotifications.filter((n) => !n.read).length,
      }

    case 'MARK_ALL_NOTIFICATIONS_READ':
      return {
        ...state,
        notifications: state.notifications.map((notification) => ({
          ...notification,
          read: true,
        })),
        unreadCount: 0,
      }

    case 'CLEAR_NOTIFICATION':
      const filteredNotifications = state.notifications.filter(
        (notification) => notification.id !== action.payload,
      )

      return {
        ...state,
        notifications: filteredNotifications,
        unreadCount: filteredNotifications.filter((n) => !n.read).length,
      }

    // Nouveau: Gérer la suppression de notifications de réaction quand la réaction est retirée
    case 'REMOVE_POST_REACTION_NOTIFICATION':
      const { postId, senderId } = action.payload
      const withoutReactionNotification = state.notifications.filter(
        (notification) =>
          !(
            notification.type === 'post_reaction' &&
            notification.postId === postId &&
            notification.sender?.id === senderId
          ),
      )

      return {
        ...state,
        notifications: withoutReactionNotification,
        unreadCount: withoutReactionNotification.filter((n) => !n.read).length,
      }

    // NOUVEAU: Mettre à jour directement le compteur de notifications non lues (via Socket.IO)
    case 'SET_UNREAD_COUNT':
      return {
        ...state,
        unreadCount: action.payload,
      }

    default:
      return state
  }
}

export default notificationsReducer
